import { QuestionAnswer } from '@capturi/api-conversations'
import { useCallback, useMemo, useState } from 'react'
import isEqual from 'react-fast-compare'

import { Guid, QaQuestionnaire } from '../../types'
import { getVisibleQuestions } from '../../utils/validation'

export const useQuestionnaireAnswers = ({
  initialAnswers,
  questionnaire,
}: {
  initialAnswers?: Record<Guid, QuestionAnswer>
  questionnaire: QaQuestionnaire
}) => {
  const [answers, setAnswers] = useState<Record<Guid, QuestionAnswer>>(
    initialAnswers || {},
  )

  const setAnswerFor = useCallback(
    (questionUid: string, answer: QuestionAnswer) => {
      setAnswers((prev) => {
        const updated = {
          ...prev,
          [questionUid]: answer,
        }

        // Use flatMap to get all visible question UIDs from all sections
        const visibleQuestionUids = new Set<string>(
          questionnaire.sections.reduce<string[]>((acc, section) => {
            section.questions.forEach((question) => {
              getVisibleQuestions(question, updated).forEach((q) =>
                acc.push(q.uid),
              )
            })
            return acc
          }, []),
        )

        // Use reduce to filter answers based on visibility
        const filteredAnswers = Object.entries(updated).reduce<
          Record<Guid, QuestionAnswer>
        >((acc, [uid, ans]) => {
          if (visibleQuestionUids.has(uid)) {
            acc[uid] = ans
          }
          return acc
        }, {})

        return filteredAnswers
      })
    },
    [questionnaire],
  )

  const getAnswerFor = useCallback(
    (questionUid: string): QuestionAnswer | undefined => {
      return answers[questionUid]
    },
    [answers],
  )

  const resetToInitialAnswers = useCallback(() => {
    if (initialAnswers) {
      setAnswers(initialAnswers)
    }
  }, [initialAnswers])

  const hasChanges = useMemo(() => {
    if (!initialAnswers) {
      // If there are no initial answers, check if we have any answers now
      return Object.keys(answers).length > 0
    }
    // Otherwise compare with initialAnswers
    return !isEqual(answers, initialAnswers)
  }, [answers, initialAnswers])

  return {
    answers,
    setAnswerFor,
    getAnswerFor,
    resetToInitialAnswers,
    hasChanges,
  }
}
